import { assertUnionArrayEquals } from "@/utils";
import {
  KeyCode,
  Action,
  CustomAction,
  LedAction,
  MouseAction,
  ConsumerKey,
} from "@/keyboard";

// Helper types for casting Action to variants
export type ActNoOp = Extract<Action, "NoOp">;
export type ActTrans = Extract<Action, "Trans">;
export type ActKeyCode = Extract<Action, { KeyCode: unknown }>;
export type ActMultipleKeyCodes = Extract<
  Action,
  { MultipleKeyCodes: unknown }
>;
export type ActMultipleActions = Extract<Action, { MultipleActions: unknown }>;
export type ActLayer = Extract<Action, { Layer: unknown }>;
export type ActDefaultLayer = Extract<Action, { DefaultLayer: unknown }>;
export type ActHoldTap = Extract<Action, { HoldTap: unknown }>;
export type HoldTapOpts = ActHoldTap["HoldTap"];
export type ActCustom = Extract<Action, { Custom: unknown }>;

export type ActCustomLed = Extract<CustomAction, { Led: unknown }>;
export type ActCustomMouse = Extract<CustomAction, { Mouse: unknown }>;
export type ActCustomConsumer = Extract<CustomAction, { Consumer: unknown }>;
export type ActCustomFirmware = Extract<CustomAction, { Firmware: unknown }>;
export type ActCustomLedCycle = Extract<LedAction, { Cycle: unknown }>;
export type ActCustomLedBrightness = Extract<
  LedAction,
  { Brightness: unknown }
>;
export type ActCustomMouseClick = Extract<MouseAction, { Click: unknown }>;
export type ActCustomMouseMove = Extract<MouseAction, { Move: unknown }>;
export type ActCustomMouseSensitivity = Extract<
  MouseAction,
  { Sensitivity: unknown }
>;

export const KEYCODES = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "Kb1",
  "Kb2",
  "Kb3",
  "Kb4",
  "Kb5",
  "Kb6",
  "Kb7",
  "Kb8",
  "Kb9",
  "Kb0",
  "Enter",
  "Escape",
  "BSpace",
  "Tab",
  "Space",
  "Minus",
  "Equal",
  "LBracket",
  "RBracket",
  "Bslash",
  "NonUsHash",
  "SColon",
  "Quote",
  "Grave",
  "Comma",
  "Dot",
  "Slash",
  "CapsLock",
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F11",
  "F12",
  "PScreen",
  "ScrollLock",
  "Pause",
  "Insert",
  "Home",
  "PgUp",
  "Delete",
  "End",
  "PgDown",
  "Right",
  "Left",
  "Down",
  "Up",
  "NumLock",
  "KpSlash",
  "KpAsterisk",
  "KpMinus",
  "KpPlus",
  "KpEnter",
  "Kp1",
  "Kp2",
  "Kp3",
  "Kp4",
  "Kp5",
  "Kp6",
  "Kp7",
  "Kp8",
  "Kp9",
  "Kp0",
  "KpDot",
  "NonUsBslash",
  "Application",
  "Power",
  "KpEqual",
  "F13",
  "F14",
  "F15",
  "F16",
  "F17",
  "F18",
  "F19",
  "F20",
  "F21",
  "F22",
  "F23",
  "F24",
  "Execute",
  "Help",
  "Menu",
  "Select",
  "Stop",
  "Again",
  "Undo",
  "Cut",
  "Copy",
  "Paste",
  "Find",
  "Mute",
  "VolUp",
  "VolDown",
  "LockingCapsLock",
  "LockingNumLock",
  "LockingScrollLock",
  "KpComma",
  "KpEqualSign",
  "Intl1",
  "Intl2",
  "Intl3",
  "Intl4",
  "Intl5",
  "Intl6",
  "Intl7",
  "Intl8",
  "Intl9",
  "Lang1",
  "Lang2",
  "Lang3",
  "Lang4",
  "Lang5",
  "Lang6",
  "Lang7",
  "Lang8",
  "Lang9",
  "AltErase",
  "SysReq",
  "Cancel",
  "Clear",
  "Prior",
  "Return",
  "Separator",
  "Out",
  "Oper",
  "ClearAgain",
  "CrSel",
  "ExSel",
  "LCtrl",
  "LShift",
  "LAlt",
  "LGui",
  "RCtrl",
  "RShift",
  "RAlt",
  "RGui",
] as const;

// While these keycodes are valid in keyberon (and so exist on KeyCode type) we ignore them,
// because they only work on Linux. Instead we use CustomAction::Consumer which is multiplatform.
export const IGNORED_KEYCODES = [
  "MediaPlayPause",
  "MediaStopCD",
  "MediaPreviousSong",
  "MediaNextSong",
  "MediaEjectCD",
  "MediaVolUp",
  "MediaVolDown",
  "MediaMute",
  "MediaWWW",
  "MediaBack",
  "MediaForward",
  "MediaStop",
  "MediaFind",
  "MediaScrollUp",
  "MediaScrollDown",
  "MediaEdit",
  "MediaSleep",
  "MediaCoffee",
  "MediaRefresh",
  "MediaCalc",
] as const;

type AllKeycodes = typeof KEYCODES | typeof IGNORED_KEYCODES;

// TODO: add more media keys
export const CONSUMER_KEYS = [
  "ScanPreviousTrack",
  "ScanNextTrack",
  "PlayPause",
  "Stop",
  "Mute",
  "VolumeIncrement",
  "VolumeDecrement",
] as const;

export const IGNORED_CONSUMER_KEYS = [
  "Unassigned",
  "ConsumerControl",
  "NumericKeyPad",
  "ProgrammableButtons",
  "Microphone",
  "Headphone",
  "GraphicEqualizer",
  "Plus10",
  "Plus100",
  "AmPm",
  "Power",
  "Reset",
  "Sleep",
  "SleepAfter",
  "SleepMode",
  "Illumination",
  "FunctionButtons",
  "Menu",
  "MenuPick",
  "MenuUp",
  "MenuDown",
  "MenuLeft",
  "MenuRight",
  "MenuEscape",
  "MenuValueIncrease",
  "MenuValueDecrease",
  "DataOnScreen",
  "ClosedCaption",
  "ClosedCaptionSelect",
  "VcrTv",
  "BroadcastMode",
  "Snapshot",
  "Still",
  "Selection",
  "AssignSelection",
  "ModeStep",
  "RecallLast",
  "EnterChannel",
  "OrderMovie",
  "Channel",
  "MediaSelection",
  "MediaSelectComputer",
  "MediaSelectTV",
  "MediaSelectWWW",
  "MediaSelectDVD",
  "MediaSelectTelephone",
  "MediaSelectProgramGuide",
  "MediaSelectVideoPhone",
  "MediaSelectGames",
  "MediaSelectMessages",
  "MediaSelectCD",
  "MediaSelectVCR",
  "MediaSelectTuner",
  "Quit",
  "Help",
  "MediaSelectTape",
  "MediaSelectCable",
  "MediaSelectSatellite",
  "MediaSelectSecurity",
  "MediaSelectHome",
  "MediaSelectCall",
  "ChannelIncrement",
  "ChannelDecrement",
  "MediaSelectSAP",
  "VCRPlus",
  "Once",
  "Daily",
  "Weekly",
  "Monthly",
  "Play",
  "Pause",
  "Record",
  "FastForward",
  "Rewind",
  "Eject",
  "RandomPlay",
  "SelectDisc",
  "EnterDisc",
  "Repeat",
  "Tracking",
  "TrackNormal",
  "SlowTracking",
  "FrameForward",
  "FrameBack",
  "Mark",
  "ClearMark",
  "RepeatFromMark",
  "ReturnToMark",
  "SearchMarkForward",
  "SearchMarkBackwards",
  "CounterReset",
  "ShowCounter",
  "TrackingIncrement",
  "TrackingDecrement",
  "StopEject",
  "PlaySkip",
  "Volume",
  "Balance",
  "Bass",
  "Treble",
  "BassBoost",
  "SurroundMode",
  "Loudness",
  "MPX",
  "SpeedSelect",
  "PlaybackSpeed",
  "StandardPlay",
  "LongPlay",
  "ExtendedPlay",
  "Slow",
  "FanEnable",
  "FanSpeed",
  "LightEnable",
  "LightIlluminationLevel",
  "ClimateControlEnable",
  "RoomTemperature",
  "SecurityEnable",
  "FireAlarm",
  "PoliceAlarm",
  "Proximity",
  "Motion",
  "DuressAlarm",
  "HoldupAlarm",
  "MedicalAlarm",
  "BalanceRight",
  "BalanceLeft",
  "BassIncrement",
  "BassDecrement",
  "TrebleIncrement",
  "TrebleDecrement",
  "SpeakerSystem",
  "ChannelLeft",
  "ChannelRight",
  "ChannelCenter",
  "ChannelFront",
  "ChannelCenterFront",
  "ChannelSide",
  "ChannelSurround",
  "ChannelLowFrequencyEnhancement",
  "ChannelTop",
  "ChannelUnknown",
  "SubChannel",
  "SubChannelIncrement",
  "SubChannelDecrement",
  "AlternateAudioIncrement",
  "AlternateAudioDecrement",
  "ApplicationLaunchButtons",
  "ALLaunchButtonConfigurationTool",
  "ALProgrammableButtonConfiguration",
  "ALConsumerControlConfiguration",
  "ALWordProcessor",
  "ALTextEditor",
  "ALSpreadsheet",
  "ALGraphicsEditor",
  "ALPresentationApp",
  "ALDatabaseApp",
  "ALEmailReader",
  "ALNewsreader",
  "ALVoicemail",
  "ALContactsAddressBook",
  "ALCalendarSchedule",
  "ALTaskProjectManager",
  "ALLogJournalTimecard",
  "ALCheckbookFinance",
  "ALCalculator",
  "ALAvCapturePlayback",
  "ALLocalMachineBrowser",
  "ALLanWanBrowser",
  "ALInternetBrowser",
  "ALRemoteNetworkingISPConnect",
  "ALNetworkConference",
  "ALNetworkChat",
  "ALTelephonyDialer",
  "ALLogon",
  "ALLogoff",
  "ALLogonLogoff",
  "ALTerminalLockScreensaver",
  "ALControlPanel",
  "ALCommandLineProcessorRun",
  "ALProcessTaskManager",
  "ALSelectTaskApplication",
  "ALNextTaskApplication",
  "ALPreviousTaskApplication",
  "ALPreemptiveHaltTaskApplication",
  "ALIntegratedHelpCenter",
  "ALDocuments",
  "ALThesaurus",
  "ALDictionary",
  "ALDesktop",
  "ALSpellCheck",
  "ALGrammarCheck",
  "ALWirelessStatus",
  "ALKeyboardLayout",
  "ALVirusProtection",
  "ALEncryption",
  "ALScreenSaver",
  "ALAlarms",
  "ALClock",
  "ALFileBrowser",
  "ALPowerStatus",
  "ALImageBrowser",
  "ALAudioBrowser",
  "ALMovieBrowser",
  "ALDigitalRightsManager",
  "ALDigitalWallet",
  "ALInstantMessaging",
  "ALOemFeaturesTipsTutorialBrowser",
  "ALOemHelp",
  "ALOnlineCommunity",
  "ALEntertainmentContentBrowser",
  "ALOnlineShoppingBrowser",
  "ALSmartCardInformationHelp",
  "ALMarketMonitorFinanceBrowser",
  "ALCustomizedCorporateNewsBrowser",
  "ALOnlineActivityBrowser",
  "ALResearchSearchBrowser",
  "ALAudioPlayer",
  "GenericGUIApplicationControls",
  "ACNew",
  "ACOpen",
  "ACClose",
  "ACExit",
  "ACMaximize",
  "ACMinimize",
  "ACSave",
  "ACPrint",
  "ACProperties",
  "ACUndo",
  "ACCopy",
  "ACCut",
  "ACPaste",
  "ACSelectAll",
  "ACFind",
  "ACFindAndReplace",
  "ACSearch",
  "ACGoTo",
  "ACHome",
  "ACBack",
  "ACForward",
  "ACStop",
  "ACRefresh",
  "ACPreviousLink",
  "ACNextLink",
  "ACBookmarks",
  "ACHistory",
  "ACSubscriptions",
  "ACZoomIn",
  "ACZoomOut",
  "ACZoom",
  "ACFullScreenView",
  "ACNormalView",
  "ACViewToggle",
  "ACScrollUp",
  "ACScrollDown",
  "ACScroll",
  "ACPanLeft",
  "ACPanRight",
  "ACPan",
  "ACNewWindow",
  "ACTileHorizontally",
  "ACTileVertically",
  "ACFormat",
  "ACEdit",
  "ACBold",
  "ACItalics",
  "ACUnderline",
  "ACStrikethrough",
  "ACSubscript",
  "ACSuperscript",
  "ACAllCaps",
  "ACRotate",
  "ACResize",
  "ACFlipHorizontal",
  "ACFlipVertical",
  "ACMirrorHorizontal",
  "ACMirrorVertical",
  "ACFontSelect",
  "ACFontColor",
  "ACFontSize",
  "ACJustifyLeft",
  "ACJustifyCenterH",
  "ACJustifyRight",
  "ACJustifyBlockH",
  "ACJustifyTop",
  "ACJustifyCenterV",
  "ACJustifyBottom",
  "ACJustifyBlockV",
  "ACIndentDecrease",
  "ACIndentIncrease",
  "ACNumberedList",
  "ACRestartNumbering",
  "ACBulletedList",
  "ACPromote",
  "ACDemote",
  "ACYes",
  "ACNo",
  "ACCancel",
  "ACCatalog",
  "ACBuyCheckout",
  "ACAddToCart",
  "ACExpand",
  "ACExpandAll",
  "ACCollapse",
  "ACCollapseAll",
  "ACPrintPreview",
  "ACPasteSpecial",
  "ACInsertMode",
  "ACDelete",
  "ACLock",
  "ACUnlock",
  "ACProtect",
  "ACUnprotect",
  "ACAttachComment",
  "ACDeleteComment",
  "ACViewComment",
  "ACSelectWord",
  "ACSelectSentence",
  "ACSelectParagraph",
  "ACSelectColumn",
  "ACSelectRow",
  "ACSelectTable",
  "ACSelectObject",
  "ACRedoRepeat",
  "ACSort",
  "ACSortAscending",
  "ACSortDescending",
  "ACFilter",
  "ACSetClock",
  "ACViewClock",
  "ACSelectTimeZone",
  "ACEditTimeZones",
  "ACSetAlarm",
  "ACClearAlarm",
  "ACSnoozeAlarm",
  "ACResetAlarm",
  "ACSynchronize",
  "ACSendReceive",
  "ACSendTo",
  "ACReply",
  "ACReplyAll",
  "ACForwardMsg",
  "ACSend",
  "ACAttachFile",
  "ACUpload",
  "ACDownloadSaveTargetAs",
  "ACSetBorders",
  "ACInsertRow",
  "ACInsertColumn",
  "ACInsertFile",
  "ACInsertPicture",
  "ACInsertObject",
  "ACInsertSymbol",
  "ACSaveAndClose",
  "ACRename",
  "ACMerge",
  "ACSplit",
  "ACDistributeHorizontally",
  "ACDistributeVertically",
] as const;

type AllConsumerKeys = typeof CONSUMER_KEYS | typeof IGNORED_CONSUMER_KEYS;

const _verifyKeycodes = () =>
  assertUnionArrayEquals<KeyCode, AllKeycodes>(KEYCODES);
const _verifyConsumerKeys = () =>
  assertUnionArrayEquals<ConsumerKey, AllConsumerKeys>(CONSUMER_KEYS);
